import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import ManagerLayout from "./ManagerLayout";
import { CalendarCheck, Wallet, AlertTriangle, Building2, ArrowRight, Banknote, Smartphone } from "lucide-react";
import LiveBookingsCard from "../../components/LiveBookingsCard";
import ManagerCollectionsModal from "../../components/ManagerCollectionsModal";
import { time12h } from "../../lib/dates";

export default function ManagerDashboard() {
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [collectFocus, setCollectFocus] = useState(null); // null | 'total' | 'cash' | 'upi' | 'pending_ground'

    useEffect(() => {
        const fetchAll = () => {
            Promise.all([
                api.get("/manager/stats"),
                api.get("/manager/bookings"),
            ]).then(([s, b]) => {
                setStats(s.data);
                setRecent(b.data.slice(0, 6));
            }).catch(() => {});
        };
        fetchAll();
        // Auto-refresh KPIs every 30 s so Today's Revenue updates after checkouts without manual reload
        const id = setInterval(fetchAll, 30000);
        const onFocus = () => fetchAll();
        window.addEventListener("focus", onFocus);
        return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
    }, []);

    return (
        <ManagerLayout>
            <div className="p-4 sm:p-8">
                <h1 className="font-display text-3xl font-bold uppercase text-white">Manager Dashboard</h1>
                <p className="text-slate-400 text-sm mt-1">Live snapshot for your assigned centres.</p>

                {!stats ? <div className="text-slate-500 mt-8">Loading...</div> : (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
                            <Kpi label="Today's Bookings" value={stats.today_bookings} icon={CalendarCheck} testid="mgr-kpi-bookings" />
                            <Kpi label="Today's Revenue" value={`₹${stats.today_total_revenue_incl_pending ?? stats.today_revenue}`} icon={Wallet} highlight testid="mgr-kpi-revenue" hint={`${stats.today_completed_bookings || 0} of ${stats.today_bookings} completed`} />
                            <Kpi label="Pending Balance" value={stats.pending_balance_count} icon={AlertTriangle} amber testid="mgr-kpi-pending" />
                            <Kpi label="Centres" value={stats.centres_assigned ?? "—"} icon={Building2} testid="mgr-kpi-centres" />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                            <Card title="My Collections (lifetime)">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                                    <CollectStat label="Total" amount={stats.my_collections_total} onClick={() => setCollectFocus("total")} testid="mgr-collect-card-total" />
                                    <CollectStat label="Cash" amount={stats.my_collections_cash} icon={Banknote} accent="green" onClick={() => setCollectFocus("cash")} testid="mgr-collect-card-cash" />
                                    <CollectStat label="UPI" amount={stats.my_collections_upi} icon={Smartphone} accent="blue" onClick={() => setCollectFocus("upi")} testid="mgr-collect-card-upi" />
                                    <CollectStat label="Pending Ground" amount={stats.pending_ground_collection ?? 0} icon={AlertTriangle} accent="amber" onClick={() => setCollectFocus("pending_ground")} testid="mgr-collect-card-pending" />
                                </div>
                                <div className="mt-3 text-[10px] text-slate-500 uppercase tracking-widest text-center">Tap any card for customer-wise breakdown</div>
                            </Card>
                            <Card title="Quick Actions">
                                <div className="space-y-2">
                                    <Link to="/manager/bookings" data-testid="mgr-quick-bookings"
                                        className="flex items-center justify-between px-4 h-12 bg-slate-950 border border-slate-800 hover:border-[#00FF66] rounded text-white">
                                        <span className="font-bold uppercase tracking-widest text-xs">Collect pending balance</span>
                                        <ArrowRight className="w-4 h-4 text-[#00FF66]" />
                                    </Link>
                                    <Link to="/manager/slots" data-testid="mgr-quick-slots"
                                        className="flex items-center justify-between px-4 h-12 bg-slate-950 border border-slate-800 hover:border-[#00FF66] rounded text-white">
                                        <span className="font-bold uppercase tracking-widest text-xs">Block / hold slots</span>
                                        <ArrowRight className="w-4 h-4 text-[#00FF66]" />
                                    </Link>
                                </div>
                            </Card>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
                            <div className="lg:col-span-1"><LiveBookingsCard endpoint="/manager/live-bookings" /></div>
                            <div className="lg:col-span-2">
                                <Card title="Recent bookings (your centres)">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-800">
                                            <th className="text-left py-2 px-2">Code</th>
                                            <th className="text-left py-2 px-2">Customer</th>
                                            <th className="text-left py-2 px-2">Centre</th>
                                            <th className="text-left py-2 px-2">Date · Time</th>
                                            <th className="text-right py-2 px-2">Paid</th>
                                            <th className="text-right py-2 px-2">Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-slate-300">
                                        {recent.length === 0 && (
                                            <tr><td colSpan={6} className="py-6 text-center text-slate-500">No bookings yet.</td></tr>
                                        )}
                                        {recent.map(b => (
                                            <tr key={b.id} className="border-b border-slate-900">
                                                <td className="py-2 px-2 font-mono text-xs text-[#00FF66]">{b.booking_code}</td>
                                                <td className="py-2 px-2">{b.user_name}</td>
                                                <td className="py-2 px-2">{b.centre_name}</td>
                                                <td className="py-2 px-2">{b.booking_date} {time12h(b.start_time)}</td>
                                                <td className="py-2 px-2 text-right text-white">₹{b.paid_amount}</td>
                                                <td className={`py-2 px-2 text-right font-bold ${b.balance_due > 0 ? "text-amber-400" : "text-slate-500"}`}>
                                                    {b.balance_due > 0 ? `₹${b.balance_due}` : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {collectFocus && (
                <ManagerCollectionsModal initialFocus={collectFocus} onClose={() => setCollectFocus(null)} />
            )}
        </ManagerLayout>
    );
}

function Kpi({ label, value, icon: Icon, highlight, amber, testid, hint }) {
    const border = amber ? "border-amber-700/50" : highlight ? "border-[#00FF66]/40" : "border-slate-800";
    const txt = amber ? "text-amber-400" : highlight ? "text-[#00FF66]" : "text-white";
    return (
        <div className={`bg-slate-900 border ${border} rounded-lg p-4 sm:p-5`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{label}</div>
                <Icon className={`w-4 h-4 ${txt}`} />
            </div>
            <div className={`mt-2 font-display text-3xl font-bold ${txt}`}>{value}</div>
            {hint && <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">{hint}</div>}
        </div>
    );
}

function Card({ title, children, className = "" }) {
    return (
        <div className={`bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-5 ${className}`}>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-4">{title}</div>
            {children}
        </div>
    );
}

function CollectStat({ label, amount, icon: Icon, accent, onClick, testid }) {
    const cls = accent === "green" ? "text-[#00FF66]" : accent === "blue" ? "text-cyan-400" : accent === "amber" ? "text-amber-400" : "text-white";
    return (
        <button onClick={onClick} data-testid={testid}
            className="bg-slate-950 border border-slate-800 hover:border-[#00FF66]/50 rounded p-3 transition cursor-pointer text-center w-full">
            <div className="flex items-center justify-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
                {Icon && <Icon className={`w-3 h-3 ${cls}`} />} {label}
            </div>
            <div className={`mt-1 font-display text-xl sm:text-2xl font-bold ${cls}`}>₹{(amount || 0).toLocaleString("en-IN")}</div>
        </button>
    );
}
